import { useEffect, useId, useRef, useState } from 'react'
import { Button, Container, Form, Message, Panel, Schema } from 'rsuite'
import { t } from 'i18next'
import Loading from '@/client/components/Loading'
import UserModel from '../../models/user'
import useQuery from '@client/utils/useQuery.ts'
import { TabListFooterErrorStyle, TabListFooterErrorWrapperStyle, TTabListFormData } from '@client/components/Controls'
import { user as authUser } from '@/client/stores/authStore'
import Format, { DisableOverlay, PasswordField, TextField } from '@client/components/FormControls.tsx'
import toastMessage from '@/client/utils/toastMessage'
import { updateLoggedInUser } from '@/client/utils'
import { serverLog } from '@client/utils/serverLog.ts'
import { useEditLock } from '@client/socket/useEditLock.tsx'
import { getSocket } from '@client/socket/socket.ts'

const userModel = new UserModel()
const { StringType } = Schema.Types

export default function EditUserProfile() {
    const { loading, error, data } = useQuery(userModel.read, { args: authUser.value._id! })
    const [formData, setFormData] = useState<TTabListFormData | null>(null)
    const formRef = useRef<any>(undefined)
    const [formError, setFormError] = useState({})

    //
    // The profile is the user's own record — the same one the user editor opens.
    //
    // It takes the same lock, so an administrator editing this user and the
    // user editing their profile cannot overwrite each other. The endpoint
    // checks the lock too; this is what makes the screen say so beforehand.
    //
    const userId = String(authUser.value?._id || '')
    const refetch = async () => {
        const res: any = await userModel.read(userId)
        if (res?.data) setFormData(res.data)
    }
    const lock = useEditLock({
        table: 'user',
        url: 'user',
        id: userId,
        tabId: useId(),
        userId,
        userName: String(authUser.value?.userName || ''),
        enabled: Boolean(userId),
        // what is on screen may be older than what the other person saved
        onAcquire: refetch,
        onUpdated: refetch,
    })
    const canEdit = lock.status === 'owned'

    useEffect(() => {
        if (!loading && data) {
            setFormData(data)
        }
    }, [loading, data])

    if (loading || formData === null) {
        return <Loading />
    }
    if (error) {
        return <Message type="error">{error.message}</Message>
    }

    const hasErrors = () => {
        const count = Object.keys(formError).length
        if (count > 0) console.log('EditUserProfile.hasErrors', formError, count)
        return count > 0
    }

    const format = Format()

    const model = Schema.Model({
        firstName: StringType().isRequired(format.required(t('First name'))),
        lastName: StringType().isRequired(format.required(t('Last name'))),
        userName: StringType()
            .addAsyncRule(
                (value, data) => {
                    return new Promise((resolve) => {
                        const _check = async () => {
                            let retCode = true
                            if (value) {
                                const result: any = await userModel.findtUserByNameOrEmail(value, data?._id || '')
                                if (result?.data) retCode = false
                            }
                            //console.debug('addAsyncRule: ', retCode)
                            resolve(retCode)
                        }
                        _check()
                    })
                },
                t('User name already in use') as string
            )
            .isRequired(format.required(t('User name'))),
        password: StringType()
            .when((schema) => {
                return StringType()
            })
            .addRule(
                (value, data) => {
                    return true
                },
                t('Passwords no match') as string
            ),
        confirmPassword: StringType()
            .when((schema) => {
                return schema.password.value
                    ? StringType().isRequired(format.required(t('Confirm password')))
                    : StringType()
            })
            .addRule((value, data) => value === data.password, t('Passwords no match') as string),
        email: StringType()
            .addAsyncRule(
                (value, data) => {
                    return new Promise((resolve) => {
                        const _check = async () => {
                            let retCode = true
                            if (value) {
                                const result: any = await userModel.findtUserByNameOrEmail(value, data?._id || '')
                                if (result?.data) retCode = false
                            }
                            console.error('addAsyncRule: ', retCode)
                            resolve(retCode)
                        }
                        _check()
                    })
                },
                t('Email already in use') as string
            )
            .isEmail(t('Invalid email address') as string),
    })

    const onSubmit = async () => {
        const { _id, ...payload } = formData
        const response = await userModel.update(_id, payload)
        //console.log(response)
        if (response?.data) {
            serverLog.info(`User ${authUser.value.userName} edited their own profile.`)
            updateLoggedInUser(response?.data)
            // what was stored, not what was typed — the server trims names
            setFormData({ ...formData, ...response.data })
            toastMessage(t('Successfully saved'))
            getSocket().emit('editing client: resourceUpdated', { table: 'user', id: userId, url: 'user' })
        } else {
            setFormError({ message: t('Unknown Error') })
        }
    }

    return (
        <Container>
            <DisableOverlay disabled={!canEdit} banner={lock.banner}>
                <Panel bordered header={t('My Profile')}>
                    <Form layout='horizontal' ref={formRef} model={model} formValue={formData} onChange={setFormData} onSubmit={onSubmit}>
                        <TextField name="firstName" label={t('First name')} autoFocus />
                        <TextField name="lastName" label={t('Last name')} />

                        <TextField name="email" label={t('Email')} />
                        <TextField name="phone" label={t('Phone')} />
                        <TextField name="userName" label={t('User name')} autoComplete="new-password" readOnly />
                        <PasswordField name="password" label={t('Password')} checkStrength={true} />
                        <PasswordField name="confirmPassword" label={t('Confirm password')} />

                        <Form.Group>
                            {hasErrors() && (
                                <span style={TabListFooterErrorWrapperStyle}>
                                    <span style={TabListFooterErrorStyle}>{t('Check your inputs')}</span>
                                </span>
                            )}
                            <Button
                                appearance="ghost"
                                onClick={() => formRef.current.checkAsync().then((r: any) => !r.hasError && onSubmit())}
                            >
                                {t('Apply')}
                            </Button>
                        </Form.Group>
                    </Form>
                </Panel>
            </DisableOverlay>
        </Container>
    )
}
